import { effect, Injectable, OnDestroy } from '@angular/core'
import { combineLatest, Subject, takeUntil } from 'rxjs'
import { MapService } from 'src/app/core/services/map.service'
import {
  ExpressionSpecification,
  FillLayerSpecification,
  LineLayerSpecification,
  SymbolLayerSpecification,
} from 'maplibre-gl'
import { AppStateService } from 'src/app/core/services/app-state.service'
import { SimulationWarningsEditorDataService } from 'src/app/simulation/services/simulation-warnings-editor-data.service'
import { emptyGeoJsonSource } from 'src/app/core/utils/source-utils'
import { Feature, Point } from 'geojson'
import { SimWarningDataModel } from '../models/sim-warning-creation.model'
import { warningFilter } from '../utils/data-driven-property'
import { MapIcons } from 'src/app/core/enums/map-icons.enum'
import { Layers } from '../layers-config/layers'
import { MapEventsService } from 'src/app/core/services/map-events.service'

@Injectable()
export class SimulationEditorLayerService implements OnDestroy {
  public layerId = Layers.SimEditorLayer.name
  public sourceId = `${this.layerId}-src`

  private readonly layerLinesId = `${this.layerId}-lines`
  private readonly layerHexagonsId = `${this.layerId}-hexagons`
  private readonly layerSymbolsId = `${this.layerId}-symbols`

  private readonly sourceLinesId = `${this.layerLinesId}-source`
  private readonly sourceHexagonsId = `${this.layerHexagonsId}-source`
  private readonly sourceSymbolsId = `${this.layerSymbolsId}-source`

  private isLayerVisible = false
  private readonly cleanUp$ = new Subject<void>()
  private layerInitialized = false
  private selectedFeatureId: string = ''

  private readonly warningIconExpression: ExpressionSpecification = [
    'case',
    warningFilter.accidentFilter,
    MapIcons.TVA,
    warningFilter.bdvFilter,
    MapIcons.TBDV,
    warningFilter.csFilter,
    MapIcons.TCS,
    warningFilter.lvFilter,
    MapIcons.TLV,
    warningFilter.tlFilter,
    MapIcons.TTL,
    warningFilter.swFilter,
    MapIcons.TSW,
    warningFilter.hrFilter,
    MapIcons.THR,
    warningFilter.hsFilter,
    MapIcons.THS,
    warningFilter.prFilter,
    MapIcons.TPR,
    warningFilter.wwdFilter,
    MapIcons.TWWD,
    warningFilter.iorFilter,
    MapIcons.TIOR,
    warningFilter.alFilter,
    MapIcons.TAL,
    warningFilter.blFilter,
    MapIcons.TBL,
    warningFilter.svFilter,
    MapIcons.TSV,
    warningFilter.tjtFilter,
    MapIcons.TTJT,
    '',
  ]

  private readonly linesLayerSpec: LineLayerSpecification = {
    id: this.layerLinesId,
    type: 'line',
    source: this.sourceLinesId,
    layout: {
      'line-join': 'round',
      'line-cap': 'round',
      visibility: 'none',
    },
    paint: {
      'line-color': ['case', ['==', ['get', 'valid'], false], '#d9534f', '#007bff'],
      'line-opacity': ['case', ['==', ['get', 'valid'], false], 0.95, 0.9],
      'line-width': this.getSelectedFeatureLineWidthExpression(this.selectedFeatureId),
    },
  }

  private readonly hexagonsLayerSpec: FillLayerSpecification = {
    id: this.layerHexagonsId,
    type: 'fill',
    source: this.sourceHexagonsId,
    paint: {
      'fill-color': ['case', ['==', ['get', 'valid'], false], 'rgba(217, 83, 79, 0.55)', '#007bff'],
      'fill-opacity': this.getSelectedFeatureHexagonOpacityExpression(this.selectedFeatureId),
      'fill-outline-color': ['case', ['==', ['get', 'valid'], false], '#d9534f', '#0044cc'],
    },
    layout: {
      visibility: 'none',
    },
  }

  private readonly symbolsLayerSpec: SymbolLayerSpecification = {
    id: this.layerSymbolsId,
    type: 'symbol',
    source: this.sourceSymbolsId,
    layout: {
      'icon-anchor': 'bottom',
      'icon-allow-overlap': true,
      'icon-image': this.warningIconExpression,
      'icon-size': this.getSelectedFeatureIconSizeExpression(this.selectedFeatureId),
      'text-field': ['case', ['==', ['get', 'valid'], false], '?', ''],
      'text-size': ['case', ['==', ['get', 'valid'], false], 11, 0],
      'text-anchor': 'top',
      'text-offset': [0, -0.5],
      'text-allow-overlap': true,
      'text-font': ['Open Sans Bold'],
      visibility: 'none',
    },
    paint: {
      'icon-opacity': ['case', ['==', ['get', 'valid'], false], 0.6, 1],
      'text-color': '#ffffff',
      'text-halo-color': ['case', ['==', ['get', 'valid'], false], '#d9534f', 'rgba(0,0,0,0)'],
      'text-halo-width': ['case', ['==', ['get', 'valid'], false], 2, 0],
    },
  }

  constructor(
    private readonly mapService: MapService,
    private readonly appState: AppStateService,
    private readonly editorDataService: SimulationWarningsEditorDataService,
    private readonly mapEventsService: MapEventsService,
  ) {
    if (mapService.isMapReady) {
      this.initializeLayer()
    } else {
      this.mapService.mapReadyChanged.subscribe(() => {
        this.initializeLayer()
      })
    }
    this.appState.stateChanged.pipe(takeUntil(this.cleanUp$)).subscribe(() => {
      if (this.layerInitialized) {
        this.evaluateState()
      }
    })

    this.editorDataService.validWarnings$.pipe(takeUntil(this.cleanUp$)).subscribe(() => {
      if (this.isLayerVisible) {
        this.updateLayer()
      }
    })

    this.editorDataService.corruptedWarnings$.pipe(takeUntil(this.cleanUp$)).subscribe(() => {
      if (this.isLayerVisible) {
        this.updateLayer()
      }
    })
  }

  ngOnDestroy(): void {
    this.cleanUp$.next()
    if (!this.mapService.isMapReady) return
    this.mapService.map.off('styledata', this.onStyleData)
    this.mapEventsService.detachMapEvents({ layerId: this.layerSymbolsId })
    if (!this.layerInitialized) return

    this.mapService.map.removeLayer(this.layerSymbolsId)
    this.mapService.map.removeSource(this.sourceSymbolsId)
    this.mapService.map.removeLayer(this.layerHexagonsId)
    this.mapService.map.removeSource(this.sourceHexagonsId)
    this.mapService.map.removeLayer(this.layerLinesId)
    this.mapService.map.removeSource(this.sourceLinesId)
  }

  private initializeLayer(): void {
    this.mapService.map.addSource(this.sourceLinesId, {
      ...emptyGeoJsonSource,
      promoteId: 'featureId',
    })
    this.mapService.map.addSource(this.sourceHexagonsId, {
      ...emptyGeoJsonSource,
      promoteId: 'featureId',
    })
    this.mapService.map.addSource(this.sourceSymbolsId, {
      ...emptyGeoJsonSource,
      promoteId: 'featureId',
    })

    this.mapService.map.addLayer(this.linesLayerSpec)
    this.mapService.map.addLayer(this.hexagonsLayerSpec)
    this.mapService.map.addLayer(this.symbolsLayerSpec)
    this.mapEventsService.registerForMapEvents({ layerId: this.layerSymbolsId }, true)
    this.mapEventsService.registerForMapEvents({ layerId: this.layerHexagonsId }, true)
    this.mapEventsService.registerForMapEvents({ layerId: this.layerLinesId }, true)

    this.layerInitialized = true
    this.mapService.map.on('styledata', this.onStyleData)
    this.evaluateState()
  }

  // re-initializes layers after a map style reload removes all user-added layers
  private readonly onStyleData = (): void => {
    if (this.layerInitialized && !this.mapService.map.getLayer(this.layerLinesId)) {
      this.layerInitialized = false
      this.initializeLayer()
    }
  }

  private evaluateState(): void {
    const simulationEditor = this.appState.getState().simulationEditor
    const isEditorEnabled =
      simulationEditor === true ||
      (simulationEditor as unknown as string) === 'true' ||
      this.appState.getState().layers?.includes(Layers.SimEditorLayer.name)

    if (isEditorEnabled) {
      this.setVisibility(true)
    } else {
      this.setVisibility(false)
    }

    if (this.appState.hasSelectedFeatureIdChanged()) {
      this.selectedFeatureId = this.appState.getState().selectedFeatureId ?? ''
      this.updateSelectedFeatureLayout()
    }

    if (!this.isLayerVisible) return

    this.updateLayer()
  }

  private getSelectedFeatureIconSizeExpression(selectedFeatureId: string): ExpressionSpecification {
    return ['case', ['==', ['get', 'featureId'], selectedFeatureId || ''], 0.5, 0.35]
  }

  private getSelectedFeatureLineWidthExpression(
    selectedFeatureId: string,
  ): ExpressionSpecification {
    return [
      'interpolate',
      ['linear'],
      ['zoom'],
      10,
      [
        'case',
        ['boolean', ['feature-state', 'hover'], false],
        ['case', ['==', ['get', 'featureId'], selectedFeatureId || ''], 8, 5],
        ['==', ['get', 'featureId'], selectedFeatureId || ''],
        6,
        2,
      ],
      15,
      [
        'case',
        ['boolean', ['feature-state', 'hover'], false],
        ['case', ['==', ['get', 'featureId'], selectedFeatureId || ''], 14, 9],
        ['==', ['get', 'featureId'], selectedFeatureId || ''],
        10,
        4,
      ],
      20,
      [
        'case',
        ['boolean', ['feature-state', 'hover'], false],
        ['case', ['==', ['get', 'featureId'], selectedFeatureId || ''], 18, 12],
        ['==', ['get', 'featureId'], selectedFeatureId || ''],
        16,
        8,
      ],
    ]
  }

  private getSelectedFeatureHexagonOpacityExpression(
    selectedFeatureId: string,
  ): ExpressionSpecification {
    return [
      'case',
      ['boolean', ['feature-state', 'hover'], false],
      [
        'case',
        ['==', ['get', 'featureId'], selectedFeatureId || ''],
        ['case', ['==', ['get', 'valid'], false], 0.75, 0.6],
        ['case', ['==', ['get', 'valid'], false], 0.5, 0.4],
      ],
      ['==', ['get', 'featureId'], selectedFeatureId || ''],
      ['case', ['==', ['get', 'valid'], false], 0.45, 0.35],
      ['case', ['==', ['get', 'valid'], false], 0.25, 0.2],
    ]
  }

  private updateSelectedFeatureLayout(): void {
    if (!this.mapService.isMapReady || !this.layerInitialized) return

    this.mapService.map.setLayoutProperty(
      this.layerSymbolsId,
      'icon-size',
      this.getSelectedFeatureIconSizeExpression(this.selectedFeatureId),
    )

    this.mapService.map.setPaintProperty(
      this.layerLinesId,
      'line-width',
      this.getSelectedFeatureLineWidthExpression(this.selectedFeatureId),
    )

    this.mapService.map.setPaintProperty(
      this.layerHexagonsId,
      'fill-opacity',
      this.getSelectedFeatureHexagonOpacityExpression(this.selectedFeatureId),
    )
  }

  public updateLayer(): void {
    if (!this.mapService.isMapReady) return

    const allWarnings = [
      this.editorDataService.getValidWarnings(),
      this.editorDataService.getCorruptedWarnings(),
    ].flat()

    const validLineFeatures = allWarnings.filter((f) => f.geometry?.type === 'LineString')
    const validHexagonFeatures = allWarnings.filter((f) => f.geometry?.type === 'Polygon')

    const validSymbolFeatures = allWarnings
      .map((f) => this.toPointFeature(f))
      .filter((f): f is Feature<Point, SimWarningDataModel & { name: string }> => f !== null)

    this.mapService.setGeoJsonData(this.sourceLinesId, {
      type: 'FeatureCollection',
      features: validLineFeatures,
    })
    this.mapService.setGeoJsonData(this.sourceHexagonsId, {
      type: 'FeatureCollection',
      features: validHexagonFeatures,
    })
    this.mapService.setGeoJsonData(this.sourceSymbolsId, {
      type: 'FeatureCollection',
      features: validSymbolFeatures,
    })
  }

  public setVisibility(isVisible: boolean): void {
    this.isLayerVisible = isVisible
    if (!this.mapService.isMapReady || !this.layerInitialized) return

    this.mapService.setLayerVisibility(this.layerLinesId, isVisible)
    this.mapService.setLayerVisibility(this.layerHexagonsId, isVisible)
    this.mapService.setLayerVisibility(this.layerSymbolsId, isVisible)
  }

  private toPointFeature(
    feature: Feature<any, SimWarningDataModel>,
  ): Feature<Point, SimWarningDataModel & { name: string }> | null {
    const longitude = Number(feature.properties?.longitude)
    const latitude = Number(feature.properties?.latitude)

    if (!Number.isFinite(longitude) || !Number.isFinite(latitude)) {
      return null
    }

    return {
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [longitude, latitude] },
      properties: {
        ...feature.properties,
        name: feature.properties.warningName,
      },
    }
  }
}
